'use client'

import { useTranslations, useLocale } from 'next-intl'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Handshake, ExternalLink } from 'lucide-react'

export default function PartnersSection() {
  const t = useTranslations('partners')
  const locale = useLocale()

  const partners = [
    {
      key: 'validg',
      url: 'https://validg.ch',
      productHref: `/${locale}/products/agodoc`,
      gradient: 'from-secondary-600 to-primary-600',
    },
    {
      key: 'validEducation',
      url: null,
      productHref: `/${locale}/products/learncoach`,
      gradient: 'from-teal-500 to-primary-700',
    },
  ]

  return (
    <section id="partners" className="section-padding bg-secondary-50 dark:bg-secondary-800">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-secondary-900 dark:text-white flex items-center justify-center">
            <Handshake className="w-8 h-8 mr-3 text-primary-600" />
            {t('sectionTitle')}
          </h2>
          <p className="text-base sm:text-lg text-secondary-600 dark:text-secondary-300 max-w-3xl mx-auto">
            {t('sectionSubtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
          {partners.map((partner, index) => (
            <motion.div
              key={partner.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-secondary-900 rounded-2xl shadow-lg overflow-hidden"
            >
              <div className={`h-2 bg-gradient-to-r ${partner.gradient}`} />
              <div className="p-6 sm:p-8">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-bold text-secondary-900 dark:text-white">
                    {t(`${partner.key}.name`)}
                  </h3>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                    {t(`${partner.key}.role`)}
                  </span>
                </div>
                <p className="text-secondary-600 dark:text-secondary-400 mb-6 leading-relaxed">
                  {t(`${partner.key}.description`)}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    href={partner.productHref}
                    className="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    {t(`${partner.key}.product`)}
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                  {partner.url && (
                    <a
                      href={partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-secondary-600 dark:text-secondary-400 font-semibold hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {partner.url.replace('https://', '')}
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
